import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { http, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, obj=user
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch (err) {
        // 401 on initial /me is expected when logged out; log others for visibility
        if (err?.response?.status && err.response.status !== 401) {
          console.warn("[auth] initial session check failed:", err.message);
        }
        if (!cancelled) setUser(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      await http.post("/auth/login", { email, password });
      const { data } = await http.get("/auth/me");   // enriched: profile_complete, photo
      setUser(data);
      // On every successful login, default the studio experience to the "premium" theme.
      try {
        localStorage.setItem("dhp-theme", "premium");
        window.dispatchEvent(new CustomEvent("dhp:theme", { detail: "premium" }));
      } catch { /* ignore storage errors */ }
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      // 401 is expected when the session has expired; anything else deserves visibility.
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("[auth] refreshMe failed:", err.message);
      }
      return null;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await http.post("/auth/logout");
    } catch (err) {
      console.warn("[auth] logout request failed:", err.message);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, refreshMe, error }),
    [user, login, logout, refreshMe, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
